import { Download } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@shared/components/ui/Button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shared/components/ui/Tooltip';
import { cn } from '@shared/components/ui/cn';

import { useVaultFileStatus } from '../../hooks/use-vault-file-link';

import { formatAndSize, type SentImage } from './file-meta';

const REVEAL_ON_HOVER =
  'opacity-0 transition-opacity group-hover/tile:opacity-100 group-has-focus-visible/tile:opacity-100';

interface ImageTileProps {
  image: SentImage;
  label: string;
  onOpen: (opener: HTMLButtonElement) => void;
  caption?: boolean;
  moreCount?: number;
  className?: string;
}

export function ImageTile({
  image,
  label,
  onOpen,
  caption = false,
  moreCount = 0,
  className,
}: ImageTileProps) {
  const { t, i18n } = useTranslation('ai-cio');
  const descriptionId = useId();
  const removed = useVaultFileStatus(image.vaultFileId) === 'removed';
  const hasMore = moreCount > 0;
  const showCaption = caption && !hasMore;
  const meta = formatAndSize(image.name, image.size, t, i18n.language);
  const described = removed
    ? `${meta} · ${t('fileCard.removedFromVault', 'Removed from Vault', { ns: 'common' })}`
    : meta;
  const description = hasMore
    ? `${described} · ${t('sentFiles.moreImagesLabel', '{{count}} more images', { count: moreCount })}`
    : described;

  return (
    <div className={cn('group/tile relative', className)}>
      <button
        type="button"
        aria-label={label}
        aria-describedby={descriptionId}
        onClick={(event) => {
          if (event.detail > 1) return;
          onOpen(event.currentTarget);
        }}
        className="block size-full cursor-pointer overflow-hidden rounded-xl border bg-background outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        {image.url && (
          <img src={image.url} alt="" draggable={false} className="size-full object-cover" />
        )}
        <span id={descriptionId} className="sr-only">
          {description}
        </span>
      </button>
      {hasMore && (
        <div
          aria-hidden="true"
          dir="ltr"
          className="pointer-events-none absolute inset-0 grid place-items-center rounded-xl bg-neutral-950/55 text-xl font-semibold text-white"
        >
          {t('sentFiles.moreImages', '+{{count}}', { count: moreCount })}
        </div>
      )}
      {showCaption && (
        <div
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-x-0 bottom-0 h-24 rounded-b-xl bg-linear-to-b from-transparent to-black/72',
            REVEAL_ON_HOVER,
          )}
        >
          <div className="absolute inset-x-3 bottom-3 flex flex-col items-start">
            <p dir="auto" className="max-w-full truncate text-xs font-medium text-white">
              {image.name}
            </p>
            <p className="max-w-full truncate text-xs text-white/80">{meta}</p>
          </div>
        </div>
      )}
      {!hasMore && !showCaption && (
        <div
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-0 rounded-xl bg-neutral-950/10',
            REVEAL_ON_HOVER,
          )}
        />
      )}
      {!hasMore && !removed && image.url && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              asChild
              variant="secondary"
              size="icon-sm"
              className={cn(
                'pointer-events-none absolute end-2 top-2 rounded-lg group-hover/tile:pointer-events-auto',
                REVEAL_ON_HOVER,
              )}
            >
              <a
                href={image.url}
                download={image.name}
                aria-label={t('fileCard.downloadNamed', 'Download {{name}}', {
                  ns: 'common',
                  name: image.name,
                })}
              >
                <Download aria-hidden="true" />
              </a>
            </Button>
          </TooltipTrigger>
          <TooltipContent sideOffset={4}>
            {t('fileCard.download', 'Download', { ns: 'common' })}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

interface ImagePreviewProps {
  image: SentImage;
  onOpen: (opener: HTMLButtonElement) => void;
}

export function ImagePreview({ image, onOpen }: ImagePreviewProps) {
  const { t } = useTranslation('ai-cio');

  return (
    <ImageTile
      image={image}
      caption
      label={t('sentFiles.openImageNamed', 'Open {{name}}', { name: image.name })}
      onOpen={onOpen}
      className="h-[180px] w-[280px] max-w-full"
    />
  );
}
